import Canva from "../Canva/Canva";
import { createCableLine } from "../Canva/CableLine";
import Konva from "../Canva/Konva";
import Polygon from "../Canva/Polygon";
import {
  buildMatColorMap,
  MAT_LAYOUT_COLORS,
} from "../Common/matLayoutColors";

// The result is displayed at about 350px wide in the generated PDF. A 4x
// export remains print quality while keeping the data URL below the browser's
// localStorage quota, even with anti-aliased rounded cable bends.
const RESULT_EXPORT_PIXEL_RATIO = 4;
const RESULT_EXPORT_PADDING = 20;

const funcNext = (
  loading,
  setLoading,
  stateCanva,
  store,
  setError,
  navigate,
  mode,
  showWarning
) => {
  if (loading !== 0) return;

  const room_area = store.polygon
    ? Polygon.getAreaByShoelaceFormula(store.polygon.points)
    : 0;

  // Allow: cable if <= 20 m² (types 2/3), or any size for mats (type 1)
  if (
    (room_area <= 20 && (store.floorType === 2 || store.floorType === 3)) ||
    store.floorType === 1
  ) {
    if (store.thermo.length <= 0) {
      showWarning("Choose thermostat to continue");
      return;
    }

    // ----- Thermostat proximity check to cold spots -----
    function generateCirclePoints(originX, originY, radius, numPoints) {
      const points = [];
      for (let i = 0; i < numPoints; i++) {
        const angle = ((2 * Math.PI) / numPoints) * i;
        const x = originX + radius * Math.cos(angle);
        const y = originY + radius * Math.sin(angle);
        const ix = originX - radius * Math.cos(angle);
        const iy = originY - radius * Math.sin(angle);
        points.push([ix, iy, x, y]);
      }
      return points;
    }

    const centerX = store.thermo[0].x();
    const centerY = store.thermo[0].y();
    const circleRadius = (Polygon.scale / 100) * 15;
    const numberOfPoints = 10;

    const thermostatsRays = generateCirclePoints(
      centerX,
      centerY,
      circleRadius,
      numberOfPoints
    );

    let isMatching = false;

    stateCanva.stage.find(".cold_spot").forEach(function (spot) {
      spot.fill("#5A659A");
      const points = spot.points();

      // close polygon
      points.push(points[0]);
      points.push(points[1]);

      const linesArray = [];
      for (let i = 0; i < points.length; i += 2) {
        if (points[i + 3]) {
          linesArray.push([
            points[i],
            points[i + 1],
            points[i + 2],
            points[i + 3],
          ]);
        } else {
          break;
        }
      }

      thermostatsRays.forEach((ray) => {
        linesArray.forEach((chunk) => {
          if (
            Canva.intersects(
              ray[0],
              ray[1],
              ray[2],
              ray[3],
              spot.x() + chunk[0],
              spot.y() + chunk[1],
              spot.x() + chunk[2],
              spot.y() + chunk[3]
            )
          ) {
            spot.fill("red");
            isMatching = true;
          }
        });
      });
    });

    if (isMatching) {
      showWarning("Thermostat is too close to the cold spot.");
      return;
    }

    // ----- Build request payload & call BE -----
    setLoading(1);
    stateCanva.cleanPolygon();

    let sum_spot_area = 0;
    const spots = store.spots.map((itm) => {
      const area = Polygon.getAreaByShoelaceFormula(itm.points);
      sum_spot_area += area;
      return {
        spot_point: itm.getPointsCoords(),
        spot_area: area,
        spot_shift: { x: itm.x, y: itm.y },
        type: itm.type,
      };
    });

    const thermo = store.thermo.map((itm) => ({
      coord: { x: itm.x(), y: itm.y(), id: itm.attrs.id },
    }));

    const dataF = {
      productType:
        store.floorType === 2 || store.floorType === 3 ? "cable" : "mat",
      room_points: store.polygon.getPointsCoords(),
      room_shift: { x: store.polygon.x, y: store.polygon.y },
      room_heated_area: room_area - sum_spot_area,
      floor1: store.floor1,
      spots: spots,
      thermostats: thermo,
      pixels_per_meter: Canva.scale,
      new_algorithm: localStorage.getItem("calc_type") === "true" ? true : false,
    };

    if (store.floorType === 2) dataF.cableType = "t5mm";
    else if (store.floorType === 3) dataF.cableType = "t3mm";

    fetch(process.env.REACT_APP_BE_HOST + "/materials", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dataF),
    })
      .then((response) => {
        if (!response.ok) {
          // Hard fail – keep overlay visible until user changes something
          setLoading(0);
          setError(1);
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((resp) => {
        // Validate payload before continuing
        const hasWires =
          resp &&
          resp.calculatedData &&
          Array.isArray(resp.calculatedData.wires) &&
          resp.calculatedData.wires.length > 0;

        if (!hasWires) {
          setLoading(0);
          setError(1); // show persistent error overlay
          return;
        }

        resp.matColorsById = buildMatColorMap(resp);

        resp.calculatedData.wires.forEach((_, i) => {
          let electric_cable = null;

          if (resp.calculatedData.wires[i].wire_points) {
            if (resp.calculatedData.wires[i].wire_points.length > 6) {
              electric_cable = [
                resp.calculatedData.wires[i].wire_points.shift(),
                resp.calculatedData.wires[i].wire_points.shift(),
                resp.calculatedData.wires[i].wire_points.shift(),
                resp.calculatedData.wires[i].wire_points.shift(),
                resp.calculatedData.wires[i].wire_points[0],
                resp.calculatedData.wires[i].wire_points[1],
              ];
            }
          }

          if (electric_cable) {
            const e_line = createCableLine(Konva, {
              x: 0,
              y: 0,
              points: electric_cable,
              stroke: "grey",
              name: "wire",
            });
            stateCanva.layer1.add(e_line);
          }

          const line = createCableLine(Konva, {
            x: 0,
            y: 0,
            points: resp.calculatedData.wires[i].wire_points,
            stroke: MAT_LAYOUT_COLORS[i] || "red",
            name: "wire",
          });

          stateCanva.layer1.add(line);
        });

        const stagePoly = stateCanva.stage.find(".poly");
        const stageThemo = stateCanva.stage.find(".thermo");
        const stageSpot = stateCanva.stage.find(".cold_spot");
        const stageWire = stateCanva.stage.find(".wire");
        const stageRef = stateCanva.stage.find(".ref_point");

        const group = new Konva.Group();
        stagePoly.forEach((itm) => group.add(itm));
        stageSpot.forEach((itm) => group.add(itm));
        stageWire.forEach((itm) => group.add(itm));
        stageRef.forEach((itm) => group.add(itm));
        stageThemo.forEach((itm) => {
          group.add(
            itm.clone({
              scaleX: itm.scaleX() * 0.5,
              scaleY: itm.scaleY() * 0.5,
            })
          );
        });

        // Use the room outline as the single source of truth for both the
        // watermark centre and the exported image crop. Other nodes (especially
        // a rotated watermark) must not pull the plan towards a canvas corner.
        const planBounds =
          stagePoly.length > 0
            ? stagePoly[0].getClientRect({ relativeTo: group })
            : group.getClientRect({ relativeTo: group });
        const exportBounds = {
          x: planBounds.x - RESULT_EXPORT_PADDING,
          y: planBounds.y - RESULT_EXPORT_PADDING,
          width: planBounds.width + RESULT_EXPORT_PADDING * 2,
          height: planBounds.height + RESULT_EXPORT_PADDING * 2,
        };

        // Helper to finalise export, set store, persist, and navigate
        const exportAndNavigate = () => {
          try {
            store.final_img = group.toDataURL({
              pixelRatio: RESULT_EXPORT_PIXEL_RATIO,
              ...exportBounds,
            });
          } catch (e) {
            // If export fails, surface error and stop
            setLoading(0);
            setError(1);
            console.warn("Export failed", e);
            return;
          }

          store.mat_data = resp;

          // Persist for PDFGen / cold loads
          try {
            localStorage.setItem("store", JSON.stringify(store));
          } catch (e) {
            console.warn("Failed to persist store", e);
          }

          const img_final = new Image();
          img_final.onload = () => {
            store.final_img_w = img_final.width;
            store.final_img_h = img_final.height;
            setLoading(0);
            navigate("/result?mode=" + mode);
          };
          img_final.onerror = () => {
            setLoading(0);
            setError(1);
          };
          img_final.src = store.final_img;
        };

        // Add diagonal watermark (logo) before exporting
        const logo = new Image();
        logo.onload = () => {
          try {
            const centreX = planBounds.x + planBounds.width / 2;
            const centreY = planBounds.y + planBounds.height / 2;

            // 60% of layout width; scale proportionally
            const targetWidth = planBounds.width * 0.6;
            const scaleFactor = logo.width > 0 ? targetWidth / logo.width : 0.5;

            const logoNode = new Konva.Image({
              image: logo,
              x: centreX,
              y: centreY,
              // Konva applies scale after offset, so use the logo's unscaled
              // centre here. Scaling the offset as well displaced the logo and
              // enlarged the exported image bounds to the left.
              offsetX: logo.width / 2,
              offsetY: logo.height / 2,
              opacity: 0.25, // ~8–10%
              scaleX: scaleFactor,
              scaleY: scaleFactor,
              rotation: -30, // diagonal
              listening: false,
              name: "watermark",
            });

            group.add(logoNode);
          } catch (e) {
            console.warn("Failed to apply watermark, exporting without it", e);
          }

          exportAndNavigate();
        };

        logo.onerror = () => {
          console.warn("Watermark logo failed to load; exporting without it.");
          exportAndNavigate();
        };

        logo.src = "/img/logo_main.png";
      })
      .catch((err) => {
        console.log(err);
        setLoading(0);
        setError(1); // keep the overlay visible until user changes inputs
      });
  } else {
    alert("An error occurred. Please proceed to the selection of heating mats.");
  }
};

export default funcNext;
